import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Anime } from "@/mocks/anime";
import { formatRating } from "@/utils/format";

interface HeroCarouselProps {
  items: Anime[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (next: number) => {
      if (items.length === 0) return;
      setActive((next + items.length) % items.length);
    },
    [items.length],
  );

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;

  const current = items[active];

  return (
    <section
      className="relative overflow-hidden rounded-lg bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="热门动漫焦点轮播"
    >
      <div className="relative h-[420px] w-full md:h-[520px]">
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            aria-hidden={index !== active}
          >
            <img
              src={item.banner}
              alt={`${item.title} 焦点大图`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent" />

        <div className="relative z-10 flex h-full w-full flex-col justify-end p-6 md:p-12">
          <div key={current.id} className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
              <i className="ri-trophy-line" />
              本周焦点
            </span>
            <h2 className="mt-4 font-heading text-3xl font-extrabold text-background-50 md:text-5xl">
              {current.title}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-100/90">
              <span className="flex items-center gap-1 font-semibold text-accent-300">
                <i className="ri-star-fill" />
                {formatRating(current.rating)}
              </span>
              <span className="whitespace-nowrap">{current.region}</span>
              <span className="whitespace-nowrap">{current.year} 年</span>
              <span className="whitespace-nowrap">{current.episodes} 集</span>
              <span className="whitespace-nowrap">{current.status}</span>
            </div>
            <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base">
              {current.description}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill text-lg" />
                立即观看
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
              >
                浏览全部
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(active - 1)}
          className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur-sm transition-colors hover:bg-foreground-950/70 md:left-6 md:h-11 md:w-11"
          aria-label="上一个"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          onClick={() => go(active + 1)}
          className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur-sm transition-colors hover:bg-foreground-950/70 md:right-6 md:h-11 md:w-11"
          aria-label="下一个"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-4 right-4 z-20 flex items-center gap-2 md:bottom-8 md:right-12">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => go(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === active ? "w-8 bg-primary-400" : "w-4 bg-background-50/50 hover:bg-background-50/80"
              }`}
              aria-label={`切换到第 ${index + 1} 张`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}